/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { ActionButton } from '@fluentui/react/lib/Button';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { numberToPercentageString } from 'src/core/utils/number';
import { CustomClassificationResult } from 'src/customText/core/services/customTextSwagger';
import { commonDemosMessages } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonDemoMessages';
import { resultCardStyles, resultCardTitleStyles } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonStyles';
import { getEntitiesSortOptions, sortClassesHelper, SortTypes } from 'src/languageStudio/core/utils/demosUtils';

export type VisualizedCustomClassificationResultProps = { document: string; classes: CustomClassificationResult[] };

const messages = defineMessages({
    sort: { id: 'languageStudio.visualizedCustomClassificationResult.sort', defaultMessage: 'Sort' },
    filter: { id: 'languageStudio.visualizedCustomClassificationResult.filter', defaultMessage: 'Filter' },
    noPredictedClasses: {
        id: 'languageStudio.visualizedCustomClassificationResult.noPredictedClasses',
        defaultMessage: 'There are no results found.'
    }
});

const theme = getScopedTheme('VisualizedCustomClassificationResult');
const classCardStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({ root: { width: '200px', minHeight: '60px' } });

export const VisualizedCustomClassificationResult = (props: VisualizedCustomClassificationResultProps) => {
    const { classes, document } = props;
    const { localize } = useLocalization();

    const [selectedSortType, setSelectedSortType] = React.useState<SortTypes>('confidenceAsc');

    const sortMenuItems: IContextualMenuItem[] = getEntitiesSortOptions(selectedSortType, setSelectedSortType).filter(
        item => item.key === 'confidenceAsc' || item.key === 'confidenceDesc'
    );

    const classCard = (cls: CustomClassificationResult) => (
        <Stack
            key={cls.category}
            tokens={{ childrenGap: 7 }}
            data-automation-id={cls.category}
            styles={mergeStyleSets(resultCardStyles, classCardStyles)}>
            <LuisText fontWeight={500} styles={resultCardTitleStyles(theme.defaultCardColor)}>
                {cls.category}
            </LuisText>
            <Stack>
                <LuisText variant="smallPlus">
                    {localize(commonDemosMessages.confidenceLabel)} {numberToPercentageString({ ratio: cls.confidenceScore })}
                </LuisText>
            </Stack>
        </Stack>
    );

    return (
        <Stack styles={{ root: { width: '100%' } }} verticalAlign="end" tokens={{ childrenGap: 25 }}>
            <Stack horizontal horizontalAlign="end" verticalAlign="center">
                <Stack horizontal>
                    <ActionButton
                        text={localize(messages.sort)}
                        menuProps={{ items: sortMenuItems }}
                        iconProps={{ iconName: 'Ascending' }}
                    />
                </Stack>
            </Stack>

            <Stack horizontal wrap styles={{ root: { marginTop: '15px !important' } }} tokens={{ childrenGap: 20 }}>
                {classes.length ? (
                    classes.sort((a, b) => sortClassesHelper(a, b, selectedSortType)).map(classCard)
                ) : (
                    <LuisText>{localize(messages.noPredictedClasses)}</LuisText>
                )}
            </Stack>

            <Stack tokens={{ childrenGap: 10 }}>
                <LuisText as="h3" fontWeight={500}>{localize(commonDemosMessages.originalText)}</LuisText>
                <LuisText>{document}</LuisText>
            </Stack>
        </Stack>
    );
};
